import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import {
  MobileExpandableCard,
  MobileExpandCardTrigger,
} from 'modules/shared-components/MobileExpandableCard';
import {
  ChartBarIcon,
  ClockIcon,
  ExclamationCircleIcon,
  MinusIcon,
  PlusIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import Tooltip from 'modules/shared-components/tooltip';
import { IconButton } from 'modules/shared-components/button/IconButton';
import Big from 'big.js';
import {
  DcaOrderType,
  VolatilityBotFormValues,
  VolatilityPeriod,
} from '../schema';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ContractService,
  contractServiceKeys,
} from 'api/services/httpServices/ContractService';
import { chainAsset, Chains } from 'api/types/httpsTypes/d-wallets';
import Button from 'modules/shared-components/button/button';
import { Link } from 'react-router-dom';
import { useToggle } from 'modules/utils/useToggle';
import { PreSetModal } from './PreSetsModal';
import {
  RowLabel,
  RowLabelledCheckBox,
} from '../../../components/RowLabelledInput';
import { NotificationDex } from '../../../components/alerts/notification';
import NumberInput from '../../../components/inputs/number-input';
import Checkbox from '../../../components/inputs/Checkbox';
import TextInput from '../../../components/inputs/text-input/text-input';
import { DexCard } from '../../../components/cards';
import { SortDropdown } from '../../../components/dropdown/sort-dropdown';
import { fromAmount } from '../../../components/chains/units';
import { calculateWordWidth } from 'modules/shared-components/input/number-input/calculateLengthPixel';

interface MaxFundsProps {
  am: number | string;
  ad: { address: string }[] | undefined;
  dcaO: DcaOrderType | undefined;
  isDca: boolean;
  isFromWei?: boolean;
  chain: Chains;
}

export function MaxFundsNeeded({
  am,
  ad,
  dcaO,
  isDca,
  isFromWei,
  chain,
}: MaxFundsProps) {
  const amount = am
    ? (isFromWei
        ? fromAmount(am, chain, 'weiToEther')
        : parseFloat(am.toString())) ?? 0
    : 0;
  const nOfOrders = ad ? ad?.length : 0;
  const dcaOrders = isDca ? dcaO ?? undefined : undefined;

  return Big(
    Big(amount ?? 0)
      .plus(
        dcaOrders
          ? dcaOrders
              .map((l) =>
                l.amount
                  ? isFromWei
                    ? fromAmount(l.amount, chain, 'weiToEther')
                    : parseFloat(l.amount.toString()) ?? 0
                  : 0
              )
              .reduce((acc, a) => acc + a, 0)
          : 0 ?? 0
      )
      .mul(nOfOrders ?? 0)
  )
    .toFixed(5)
    .toString();
}

export const PERIOD_OPTIONS = [
  {
    value: VolatilityPeriod.Zero,
    label: '10 Sec',
  },
  {
    value: VolatilityPeriod.One,
    label: '1 Min',
  },
  {
    value: VolatilityPeriod.Five,
    label: '5 Min',
  },
  {
    value: VolatilityPeriod.Fifteen,
    label: '15 Min',
  },
  {
    value: VolatilityPeriod.Thirty,
    label: '30 Min',
  },
  {
    value: VolatilityPeriod.Sixty,
    label: '60 Min',
  },
];

export function VolatilitySettings({
  isEdit,
  isDuplicate,
}: {
  isEdit?: boolean;
  isDuplicate?: boolean;
}) {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<VolatilityBotFormValues>();
  const [isOpen, { open, close }] = useToggle();
  const [openBoxAutoRetry, setOpenBoxAutoRetry] = useState(false);

  const botSettings = watch('botSettings.entries');

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'botSettings.entries.addresses',
    keyName: 'key',
  });

  const addresses = botSettings.addresses;
  const priceChange = botSettings.priceChange;
  const periodInMinutes = botSettings.periodInMinutes;
  const chain = watch('tradingSettings.chain');

  const isDcaActive = watch('botSettings.entries.isDcaActive');
  const dcaOrders = watch('botSettings.entries.limitOrders');
  const takeProfitActive = watch('tradingSettings.takeProfit')?.length > 0;
  const stopLossActive = watch('tradingSettings.stopLoss');

  const amount = watch('tradingSettings.buyAmount');

  const period = watch('botSettings.entries.periodInMinutes');

  useEffect(() => {
    if (!isEdit && !isDuplicate) {
      remove();
      setValue('botSettings.entries.addresses', [{ address: '' }]);
    }
  }, [chain]);

  return (
    <MobileExpandableCard
      mobileLabel="Volatility Settings"
      trigger={
        <MobileExpandCardTrigger>
          {errors?.botSettings?.entries ? (
            <div className="flex space-x-1 items-center text-sm">
              <ExclamationCircleIcon className="xxs:text-red-500 xxs:w-6 h-6" />
              <div>Missing Information</div>
            </div>
          ) : (
            <div className="flex space-x-1 items-center text-sm xxs:text-dex-white-secondary">
              <div className="">Volatility:</div>

              <div className="flex space-x-1 items-center font-semibold">
                <div>
                  <ShieldCheckIcon className="w-4 h-4" />
                </div>
                <div className="xxs:text-dex-white">
                  {addresses && addresses.length > 0 && addresses?.[0]?.address
                    ? addresses.length
                    : '--'}
                </div>
              </div>
              <div>|</div>
              <div className="flex space-x-1 items-center font-semibold">
                <div>
                  <ChartBarIcon className="w-4 h-4" />
                </div>
                <div className="xxs:font-semibold">
                  {priceChange ? (
                    <span className="xxs:text-dex-white">
                      -{Big(priceChange ?? 0).toFixed(1)}%
                    </span>
                  ) : (
                    '--'
                  )}
                </div>
              </div>
              <div>|</div>
              <div className="flex space-x-1 items-center font-semibold">
                <div>
                  <ClockIcon className="h-4 w-4" />
                </div>
                <div className="xxs:text-dex-white">
                  {periodInMinutes >= 0
                    ? PERIOD_OPTIONS.find(
                        (p) => p.value === (period as VolatilityPeriod)
                      )?.label
                    : '--'}
                </div>
              </div>
            </div>
          )}
        </MobileExpandCardTrigger>
      }
    >
      <DexCard size="xs">
        <div className="hidden lg:block font-bold xxs:text-sm xxs:text-dex-white-secondary">
          Volatility Settings
        </div>
        <fieldset className="space-y-2">
          <div className="lg:flex xxs:space-y-1 lg:space-y-0 lg:space-x-2">
            <div className="w-full lg:w-1/2 ">
              <div className="flex xxs:space-x-2">
                <Tooltip text="The price drop within the selected period that will trigger a new entry when hit.">
                  <div className="xxs:text-sm xxs:text-dex-white-secondary hover:text-dex-white">
                    {' '}
                    Price Change
                  </div>
                </Tooltip>
                <div className="flex flex-nowrap items-center">
                  <Checkbox
                    className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4"
                    id="botSettings.entries.priceChangeFromAth"
                    checked={watch('botSettings.entries.priceChangeFromAth')}
                    onChange={(ev) => {
                      setValue(
                        'botSettings.entries.priceChangeFromAth',
                        ev.target.checked
                      );
                    }}
                  />
                  <Checkbox.Label htmlFor="botSettings.entries.priceChangeFromAth">
                    <Tooltip
                      className="xxs:text-dex-white-secondary hover:text-dex-white xxs:text-sm"
                      text={`Enable this to check the price drop from its all-time high within the past ${
                        PERIOD_OPTIONS.find(
                          (p) => p.value === (period as VolatilityPeriod)
                        )?.label
                      }. 
If disabled, the drop will simply be calculated as: current price minus the price from ${
                        PERIOD_OPTIONS.find(
                          (p) => p.value === (period as VolatilityPeriod)
                        )?.label
                      } ago.`}
                    >
                      <div>From ATH</div>
                    </Tooltip>
                  </Checkbox.Label>
                </div>
              </div>

              <Controller
                name="botSettings.entries.priceChange"
                defaultValue={'' as any}
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <NumberInput
                      {...field}
                      prefix={<>-</>}
                      suffix={<>%</>}
                      error={error?.message}
                    />
                  );
                }}
              />
            </div>

            <div className="w-full lg:w-1/2 xxs:space-y-1">
              <Tooltip text="Select the time period over which the price change will be calculated. For example, if you select 5 minutes, the entry will be triggered when the price change over the last 5 minutes reaches your target.">
                <div className="xxs:text-sm xxs:text-dex-white-secondary hover:text-dex-white">
                  {' '}
                  Price Change Period
                </div>
              </Tooltip>

              <Controller
                name="botSettings.entries.periodInMinutes"
                control={control}
                render={({ field }) => (
                  <SortDropdown
                    {...field}
                    mainLabel="Select a period..."
                    options={PERIOD_OPTIONS}
                  />
                )}
              />
            </div>
          </div>
          <div className="xxs:space-y-1">
            <div className="flex justify-between items-center">
              <div className="flex space-x-1 items-center">
                <Tooltip text="These are the token addresses that the bot is enabled to trade when triggered.">
                  <div className="xxs:text-dex-white-secondary hover:text-dex-white xxs:text-sm">
                    Whitelisted tokens
                  </div>
                </Tooltip>

                <Button
                  type="button"
                  onClick={open}
                  variant="dexNeutral"
                  className="xxs:px-2 xxs:py-0.5"
                >
                  <span className="xxs:text-xs">Smart pre-sets</span>
                </Button>
                {isOpen && (
                  <PreSetModal
                    handleClose={close}
                    replace={replace}
                    chain={watch('tradingSettings.chain')}
                  />
                )}
              </div>
              <Link
                to="/dex/research?activeTab=hot-tokens"
                className="xxs:text-dex-white-secondary hover:text-dex-white"
              >
                Find hot tokens
              </Link>
            </div>
            {fields.length === 0 ? (
              <div className="flex xxs:space-x-1" key="empty">
                <Controller
                  name={`botSettings.entries.addresses.${0}.address`}
                  defaultValue={'' as any}
                  control={control}
                  render={({ field, fieldState: { error: e } }) => {
                    const { data, isLoading, error } = GetContractInfo(
                      chain,
                      field.value
                    );
                    return (
                      <TextInput
                        {...field}
                        inputSize="small"
                        suffix={
                          !field.value
                            ? ''
                            : error
                            ? '?'
                            : isLoading
                            ? '...'
                            : data?.symbol ?? '--'
                        }
                        error={e?.message}
                        hideErrorMessage={true}
                      />
                    );
                  }}
                />

                <IconButton
                  className="xxs:bg-dex-black-600 hover:bg-dex-black-500 xxs:text-dex-white-secondary hover:text-dex-white xxs:p-1 xxs:rounded"
                  type="button"
                  onClick={() => append({ address: '' })}
                >
                  <PlusIcon className="h-5 w-5" aria-hidden="true" />
                </IconButton>
              </div>
            ) : (
              fields.map((row, index) => {
                return (
                  <div className="flex xxs:space-x-1" key={row.key}>
                    <Controller
                      name={`botSettings.entries.addresses.${index}.address`}
                      control={control}
                      render={({ field, fieldState: { error: e } }) => {
                        const { data, isLoading, error } = GetContractInfo(
                          chain,
                          field.value
                        );

                        return (
                          <div className="grid grid-cols-6 space-x-1 w-full">
                            <div className="col-span-5">
                              <TextInput
                                {...field}
                                inputSize="small"
                                error={e?.message}
                                hideErrorMessage={true}
                                value={field.value}
                              />
                            </div>

                            <Link
                              target="_blank"
                              rel="noopener noreferrer"
                              to={`/dex/snipe/new/${watch(
                                'tradingSettings.chain'
                              )}/${
                                !field.value
                                  ? '??'
                                  : error
                                  ? '??'
                                  : isLoading
                                  ? '??'
                                  : field.value
                              }`}
                              className="text-dex-white-secondary hover:text-dex-white bg-dex-black-700 hover:bg-dex-black-600 rounded xxs:px-2 flex items-center justify-center "
                            >
                              {!field.value
                                ? '--'
                                : error
                                ? '?'
                                : isLoading
                                ? '...'
                                : (
                                    <div className="truncate">
                                      {data?.symbol}
                                    </div>
                                  ) ?? '--'}
                            </Link>
                          </div>
                        );
                      }}
                    />

                    {index === 0 ? (
                      <IconButton
                        className="xxs:bg-dex-black-600 hover:bg-dex-black-500 xxs:text-dex-white-secondary hover:text-dex-white xxs:p-1 xxs:rounded"
                        type="button"
                        onClick={() => append({ address: '' })}
                      >
                        <PlusIcon className="h-5 w-5" aria-hidden="true" />
                      </IconButton>
                    ) : (
                      <IconButton
                        className="xxs:bg-dex-black-600 hover:bg-dex-black-500 xxs:text-dex-white-secondary hover:text-dex-white xxs:p-1 xxs:rounded"
                        type="button"
                        onClick={() => remove(index)}
                      >
                        <MinusIcon className="h-5 w-5" aria-hidden="true" />
                      </IconButton>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="xxs:space-y-1">
            <div className="w-full xxs:space-y-1">
              <div className="xxs:space-y-1">
                <div>
                  <RowLabelledCheckBox
                    label={
                      <RowLabel
                        label="Auto-retry"
                        tooltip="Enable automatic retries for buy limit orders, take profit, and stop loss transactions. If any of these transactions fail, the bot will automatically attempt to execute the transaction again, retrying once."
                      />
                    }
                    openBox={openBoxAutoRetry}
                    setOpenBox={
                      watch('botSettings.entries.autoRetryEnabled')
                        ? () => setOpenBoxAutoRetry(!openBoxAutoRetry)
                        : undefined
                    }
                    className="xxs:py-1"
                  >
                    <Controller
                      name={'botSettings.entries.autoRetryEnabled'}
                      defaultValue={'' as any}
                      control={control}
                      render={({ field }) => {
                        return (
                          <Checkbox
                            className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4"
                            checked={
                              field.value === undefined
                                ? false
                                : (field.value as boolean)
                            }
                            onClick={field.onChange}
                            onChange={() => {
                              if (!field.value) {
                                setOpenBoxAutoRetry(true);
                                setValue(
                                  'botSettings.entries.autoRetry.buy.active',
                                  isDcaActive
                                );
                                setValue(
                                  'botSettings.entries.autoRetry.takeProfit.active',
                                  takeProfitActive
                                );
                                setValue(
                                  'botSettings.entries.autoRetry.stopLoss.active',
                                  !!stopLossActive
                                );
                                setValue(
                                  'botSettings.entries.autoRetry.buy.nRetries',
                                  1
                                );
                                setValue(
                                  'botSettings.entries.autoRetry.takeProfit.nRetries',
                                  1
                                );
                                setValue(
                                  'botSettings.entries.autoRetry.stopLoss.nRetries',
                                  1
                                );
                              } else {
                                setValue(
                                  'botSettings.entries.autoRetry.buy.active',
                                  false
                                );
                                setValue(
                                  'botSettings.entries.autoRetry.takeProfit.active',
                                  false
                                );
                                setValue(
                                  'botSettings.entries.autoRetry.stopLoss.active',
                                  false
                                );
                                setValue(
                                  'botSettings.entries.autoRetry.buy.nRetries',
                                  0
                                );
                                setValue(
                                  'botSettings.entries.autoRetry.takeProfit.nRetries',
                                  0
                                );
                                setValue(
                                  'botSettings.entries.autoRetry.stopLoss.nRetries',
                                  0
                                );
                                setOpenBoxAutoRetry(false);
                              }
                            }}
                            id="botSettings.entries.autoRetryEnabled"
                          />
                        );
                      }}
                    />
                  </RowLabelledCheckBox>
                  {openBoxAutoRetry && (
                    <div className="flex flex-col space-y-1">
                      <div className="flex xxs:space-x-1 items-center justify-between">
                        <Controller
                          name="botSettings.entries.autoRetry.buy.active"
                          defaultValue={'' as any}
                          control={control}
                          render={({ field }) => {
                            return (
                              <div className="flex space-x-1 items-center justify-end">
                                <Checkbox
                                  disabled={!isDcaActive}
                                  className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4"
                                  checked={
                                    field.value === undefined
                                      ? false
                                      : (field.value as boolean)
                                  }
                                  onChange={(e) => {
                                    if (e.currentTarget.checked) {
                                      setValue(
                                        'botSettings.entries.autoRetry.buy.active',
                                        true
                                      );
                                      setValue(
                                        'botSettings.entries.autoRetry.buy.nRetries',
                                        1
                                      );
                                    } else {
                                      setValue(
                                        'botSettings.entries.autoRetry.buy.active',
                                        false
                                      );
                                      setValue(
                                        'botSettings.entries.autoRetry.buy.nRetries',
                                        0
                                      );
                                    }
                                  }}
                                  id="botSettings.entries.autoRetry.buy.active"
                                />
                                <Tooltip text="Enable this option to automatically retry the buy transaction one more time if the swap fails.">
                                  <div className="text-xs xxs:text-dex-white-secondary hover:text-dex-white">
                                    Buy limit order
                                  </div>
                                </Tooltip>
                              </div>
                            );
                          }}
                        />

                        {watch('botSettings.entries.autoRetry.buy.active') && (
                          <div className="flex max-w-[100px]">
                            <Controller
                              name="botSettings.entries.autoRetry.buy.nRetries"
                              defaultValue={'' as any}
                              control={control}
                              render={({ field, fieldState: { error } }) => {
                                return (
                                  <NumberInput
                                    extraStyle={{
                                      paddingRight: `${
                                        calculateWordWidth('retries', 12) + 20
                                      }px`,
                                    }}
                                    {...field}
                                    inputSize="xs"
                                    suffix={<>retries</>}
                                    className="text-right"
                                    hideErrorMessage={true}
                                    error={error?.message}
                                    value={field.value as unknown as number}
                                  />
                                );
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex xxs:space-x-1 items-center justify-between">
                        <Controller
                          name="botSettings.entries.autoRetry.takeProfit.active"
                          defaultValue={'' as any}
                          control={control}
                          render={({ field }) => {
                            return (
                              <div className="flex space-x-1 items-center justify-end">
                                <Checkbox
                                  className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4"
                                  disabled={!takeProfitActive}
                                  checked={
                                    field.value === undefined
                                      ? false
                                      : (field.value as boolean)
                                  }
                                  onChange={(e) => {
                                    if (e.currentTarget.checked) {
                                      setValue(
                                        'botSettings.entries.autoRetry.takeProfit.active',
                                        true
                                      );
                                      setValue(
                                        'botSettings.entries.autoRetry.takeProfit.nRetries',
                                        1
                                      );
                                    } else {
                                      setValue(
                                        'botSettings.entries.autoRetry.takeProfit.active',
                                        false
                                      );
                                      setValue(
                                        'botSettings.entries.autoRetry.takeProfit.nRetries',
                                        0
                                      );
                                    }
                                  }}
                                  id="botSettings.entries.autoRetry.takeProfit.active"
                                />
                                <Tooltip text="Enable this option to automatically retry the take profit transaction one more time if the swap fails.">
                                  <div className="text-xs xxs:text-dex-white-secondary hover:text-dex-white">
                                    Take Profit Order
                                  </div>
                                </Tooltip>
                              </div>
                            );
                          }}
                        />
                        {!!watch(
                          'botSettings.entries.autoRetry.takeProfit.active'
                        ) && (
                          <div className="flex max-w-[100px]">
                            <Controller
                              name="botSettings.entries.autoRetry.takeProfit.nRetries"
                              defaultValue={'' as any}
                              control={control}
                              render={({ field, fieldState: { error } }) => {
                                return (
                                  <NumberInput
                                    {...field}
                                    extraStyle={{
                                      paddingRight: `${
                                        calculateWordWidth('retries', 12) + 20
                                      }px`,
                                    }}
                                    inputSize="xs"
                                    suffix={<>retries</>}
                                    className="text-right"
                                    hideErrorMessage={true}
                                    error={error?.message}
                                    value={field.value as unknown as number}
                                  />
                                );
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex xxs:space-x-1 items-center justify-between">
                        <Controller
                          name="botSettings.entries.autoRetry.stopLoss.active"
                          defaultValue={'' as any}
                          control={control}
                          render={({ field }) => {
                            return (
                              <div className="flex space-x-1 items-center justify-end">
                                <Checkbox
                                  className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4"
                                  disabled={!stopLossActive}
                                  checked={
                                    field.value === undefined
                                      ? false
                                      : (field.value as boolean)
                                  }
                                  onChange={(e) => {
                                    if (e.currentTarget.checked) {
                                      setValue(
                                        'botSettings.entries.autoRetry.stopLoss.active',
                                        true
                                      );
                                      setValue(
                                        'botSettings.entries.autoRetry.stopLoss.nRetries',
                                        1
                                      );
                                    } else {
                                      setValue(
                                        'botSettings.entries.autoRetry.stopLoss.active',
                                        false
                                      );
                                      setValue(
                                        'botSettings.entries.autoRetry.stopLoss.nRetries',
                                        0
                                      );
                                    }
                                  }}
                                  id="botSettings.entries.autoRetry.stopLoss.active"
                                />
                                <Tooltip text="Enable this option to automatically retry the stop loss transaction one more time if the swap fails.">
                                  <div className="text-xs xxs:text-dex-white-secondary hover:text-dex-white">
                                    Stop Loss Order
                                  </div>
                                </Tooltip>
                              </div>
                            );
                          }}
                        />
                        {!!watch(
                          'botSettings.entries.autoRetry.stopLoss.active'
                        ) && (
                          <div className="flex max-w-[100px]">
                            <Controller
                              name="botSettings.entries.autoRetry.stopLoss.nRetries"
                              defaultValue={'' as any}
                              control={control}
                              render={({ field, fieldState: { error } }) => {
                                return (
                                  <NumberInput
                                    extraStyle={{
                                      paddingRight: `${
                                        calculateWordWidth('retries', 12) + 20
                                      }px`,
                                    }}
                                    {...field}
                                    inputSize="xs"
                                    suffix={<>retries</>}
                                    className="text-right"
                                    hideErrorMessage={true}
                                    error={error?.message}
                                    value={field.value as unknown as number}
                                  />
                                );
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {errors?.botSettings?.entries?.addresses?.message && (
                <NotificationDex type="error">
                  {errors?.botSettings?.entries?.addresses?.message}
                </NotificationDex>
              )}
            </div>
            {CheckBoxesConfigs.map((config) => (
              <RenderCheckBox
                defaultValue={config.defaultValue}
                key={config.key}
                fieldKey={config.key}
                label={config.label}
                tooltip={config.tooltip}
                prefix={config.prefix}
                suffix={config.suffix}
              />
            ))}

            {MaxFundsNeeded({
              am: amount,
              ad: addresses,
              dcaO: dcaOrders,
              isDca: isDcaActive,
              chain,
            }) && (
              <div className="lg:hidden">
                <NotificationDex className="xxs:mx-0" type="info">
                  <div className="flex space-x-1 items-center">
                    <Tooltip text="Ensure your wallet has at least this amount of funds to open all the orders as configured. Additionally, reserve extra funds to cover gas fees.">
                      <div className="xxs:text-dex-white-secondary hover:text-dex-white">
                        Max {chainAsset(chain)} needed:
                      </div>
                    </Tooltip>
                    <div className="xxs:text-dex-white-secondary">
                      {MaxFundsNeeded({
                        am: amount,
                        ad: addresses,
                        dcaO: dcaOrders,
                        isDca: isDcaActive,
                        chain,
                      }) ?? 0}{' '}
                      {chainAsset(chain)}
                    </div>
                  </div>
                </NotificationDex>
              </div>
            )}
          </div>
        </fieldset>
      </DexCard>
    </MobileExpandableCard>
  );
}

interface RenderCheckBoxProps {
  fieldKey: string;
  label: string;
  tooltip: string;
  defaultValue: number | undefined;
  prefix: string;
  suffix: string;
}

export const CheckBoxesConfigs = [
  {
    active: false,
    key: 'maxNumberOfPositions',
    label: 'Max Open Trades',
    tooltip:
      'Enable this to limit the number of trades that can be open at the same time.',
    defaultValue: 10,
    prefix: '',
    suffix: '',
  },
  {
    active: false,
    key: 'removeTokenAfterNPositions',
    label: 'Remove Token After Trades',
    tooltip:
      'Enable this to automatically remove a token from the whitelist after a certain number of trades have been closed.',
    defaultValue: 10,
    prefix: '',
    suffix: '',
  },
  {
    active: false,
    key: 'cooloffTokenAfterCloseInSec',
    label: 'Cool Down Period',
    tooltip:
      'Enable this to add a cooldown period before buying again after a token has been sold.',
    defaultValue: 600,
    prefix: '',
    suffix: 'sec',
  },
];

const RenderCheckBox = ({
  fieldKey,
  label,
  tooltip,
  defaultValue,
  prefix,
  suffix,
}: RenderCheckBoxProps) => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<VolatilityBotFormValues>();

  const [isOpen, { open, close }] = useToggle();
  const activePath = `botSettings.entries.${fieldKey}Enabled` as any;
  const valuePath = `botSettings.entries.${fieldKey}` as any;

  const MeasureError =
    errors?.botSettings?.entries?.[
      fieldKey as keyof typeof errors.botSettings.entries
    ];

  const errorCheck =
    watch(activePath) &&
    typeof MeasureError === 'object' &&
    MeasureError !== null;

  return (
    <div key={fieldKey}>
      <RowLabelledCheckBox
        label={
          <RowLabel label={label} isError={errorCheck} tooltip={tooltip} />
        }
        openBox={isOpen}
        setOpenBox={isOpen ? close : open}
        className="xxs:py-1"
      >
        <Controller
          name={activePath}
          defaultValue={'' as any}
          control={control}
          render={({ field }) => (
            <Checkbox
              className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4"
              checked={!!field.value}
              onClick={field.onChange}
              onChange={() => {
                if (!field.value) {
                  setValue(valuePath, defaultValue);
                  open();
                } else {
                  setValue(valuePath, undefined);
                  close();
                }
              }}
              id={activePath}
            />
          )}
        />
      </RowLabelledCheckBox>

      {isOpen && (
        <Controller
          name={valuePath}
          defaultValue={'' as any}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <NumberInput
              {...field}
              inputSize="small"
              hideErrorMessage={true}
              prefix={prefix}
              suffix={suffix}
              error={error?.message}
            />
          )}
        />
      )}
    </div>
  );
};

export function GetContractInfo(
  chain: Chains,
  address: string,
  isNotValid?: boolean
) {
  const [delayedAddress, setDelayedAddress] = useState(address);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDelayedAddress(address);
    }, 250);

    return () => {
      clearTimeout(handler);
    };
  }, [address]);

  const { data, isLoading, error } = useQuery({
    queryFn: () => ContractService.getContractInfo(chain, delayedAddress),
    queryKey: contractServiceKeys.getContractInfo(chain, delayedAddress),
    enabled: !isNotValid && !!delayedAddress, // ✅ Prevents query execution when invalid
    keepPreviousData: true,
  });

  return { data, isLoading, error };
}
